import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { formatDateTime } from "../utils/datetime";
import {
  currentSubscription,
  isSupported,
  subscribePush,
  testPush,
  unsubscribePush,
} from "../push";

type ServerSubscription = {
  id: number;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
};

export default function PushNotificationsCard() {
  const supported = isSupported();
  const [serverConfigured, setServerConfigured] = useState<boolean | null>(null);
  const [enabled, setEnabled] = useState<boolean>(false);
  const [subs, setSubs] = useState<ServerSubscription[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const refresh = useCallback(async () => {
    if (!supported) return;
    const sub = await currentSubscription();
    setEnabled(!!sub);
    try {
      const r = await api.get("/api/v1/push/subscriptions");
      setSubs(r.data);
    } catch {
      setSubs([]);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    api
      .get("/api/v1/push/vapid-public-key")
      .then(() => setServerConfigured(true))
      .catch(() => setServerConfigured(false));
    refresh();
  }, [supported, refresh]);

  const onEnable = async () => {
    setBusy(true);
    setMsg("");
    const result = await subscribePush();
    setBusy(false);
    switch (result.status) {
      case "subscribed":
        setMsg("This device is now subscribed to critical-alert pushes.");
        refresh();
        break;
      case "denied":
        setMsg(
          "Permission denied. To enable later, allow notifications for this site in your browser settings.",
        );
        break;
      case "unsupported":
        setMsg("This browser doesn't support Web Push.");
        break;
      case "not-configured":
        setMsg("Push isn't configured on the server (VAPID keys not set).");
        break;
      default:
        setMsg(`Couldn't subscribe: ${result.error}`);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    setMsg("");
    const ok = await unsubscribePush();
    setBusy(false);
    setMsg(ok ? "Unsubscribed." : "Nothing to unsubscribe.");
    refresh();
  };

  const onTest = async () => {
    setBusy(true);
    setMsg("");
    try {
      // The endpoint is synchronous now and 4xx/5xx when nothing was actually
      // delivered, so a resolved call means the notification really went out —
      // it no longer just means "we tried".
      const n = await testPush();
      setMsg(
        `Delivered to ${n} device${n === 1 ? "" : "s"} — the notification should appear now.`,
      );
    } catch (e: any) {
      setMsg(e.response?.data?.detail || "Test push failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mt-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Push notifications</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          This browser doesn't support Web Push. Try Chrome, Firefox, or Safari 16.4+ on a
          modern OS.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mt-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Push notifications</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Get a phone/desktop banner when a critical alert fires, even when the dashboard tab
            is closed. Per-device — enable on each phone/laptop separately.
          </p>
        </div>
        <span
          className={
            "text-xs px-2 py-1 rounded font-semibold " +
            (enabled ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300")
          }
        >
          {enabled ? "Enabled here" : "Disabled here"}
        </span>
      </div>

      {serverConfigured === false && (
        <p className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded p-2 my-3">
          The server isn't configured for push (VAPID keys not set). An admin needs to generate
          and add them to the server <code>.env</code> before opt-in works.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!enabled ? (
          <button
            onClick={onEnable}
            disabled={busy || serverConfigured === false}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
          >
            {busy ? "Working…" : "Enable on this device"}
          </button>
        ) : (
          <>
            <button
              onClick={onTest}
              disabled={busy}
              className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
            >
              Send test notification
            </button>
            <button
              onClick={onDisable}
              disabled={busy}
              className="border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
            >
              Disable on this device
            </button>
          </>
        )}
      </div>

      {msg && <p className="text-xs text-gray-600 dark:text-gray-400 mt-3">{msg}</p>}

      {subs.length > 0 && (
        <div className="mt-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Your subscribed devices ({subs.length})</div>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            {subs.map((s) => (
              <li key={s.id} className="font-mono truncate" title={s.endpoint}>
                {s.user_agent || s.endpoint}
                {s.last_used_at && (
                  <span className="text-gray-400 dark:text-gray-500">
                    {" "}
                    · last used {formatDateTime(s.last_used_at)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
